'use client';

/**
 * FormSection — a heading for a group of fields (Complete_app §17.1 "clear
 * headings", "progressive disclosure rather than displaying every field at once").
 *
 *     <FormSection title="Parties" description="Who signs this.">…fields…</FormSection>
 *     <FormSection title="More details" collapsible defaultOpen={false}>…</FormSection>
 *
 * Collapsible sections use `<details>` so they open with the keyboard and
 * need no script; `forceOpen` keeps one open while it holds an error.
 */
import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from './cn';

export interface FormSectionProps {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Keep a collapsible section open (e.g. one of its fields has an error). */
  forceOpen?: boolean;
  /** Heading level for the title (default 2 — under the page's h1). */
  level?: 2 | 3;
  className?: string;
}

export function FormSection({
  title,
  description,
  children,
  collapsible = false,
  defaultOpen = true,
  forceOpen = false,
  level = 2,
  className,
}: FormSectionProps) {
  const H = level === 2 ? 'h2' : 'h3';
  const titleClass = level === 2 ? 'text-base font-semibold text-fg' : 'text-sm font-semibold text-fg';

  if (collapsible) {
    return (
      <details
        className={cn('group rounded-lg border border-slate-200 bg-white', className)}
        open={forceOpen || defaultOpen || undefined}
      >
        <summary className="flex cursor-pointer list-none items-start justify-between gap-3 rounded-lg px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold [&::-webkit-details-marker]:hidden">
          <span>
            <H className={titleClass}>{title}</H>
            {description ? <span className="mt-0.5 block text-sm text-fg-muted">{description}</span> : null}
          </span>
          <ChevronDown
            aria-hidden
            className="mt-0.5 h-4 w-4 shrink-0 text-fg-muted transition-transform group-open:rotate-180"
          />
        </summary>
        <div className="space-y-4 border-t border-slate-100 px-4 py-4">{children}</div>
      </details>
    );
  }

  return (
    <fieldset className={cn('min-w-0 space-y-4', className)}>
      <legend className="contents">
        <H className={titleClass}>{title}</H>
      </legend>
      {description ? <p className="-mt-3 text-sm text-fg-muted">{description}</p> : null}
      {children}
    </fieldset>
  );
}
